import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useUserProfile } from '../../contexts/UserContext';
import {
  getAdminStats, getAllUsersWithSubscription, PLANOS,
  adminSetUserPlan, adminCancelUserPlan, adminSetUserRole,
  getSystemConfig, updateSystemConfig, recalculateActiveTrials,
} from '../../services/adminService';
import { ROLE_LABELS, ROLE_COLORS, ROLES } from '../../services/userService';
import {
  getAllPosts, togglePublish, deletePost,
} from '../../services/blogService';

/* ── Helpers ── */
const fmt = (n) => (n ?? 0).toLocaleString('pt-BR');
const fmtMoeda = (n) => (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function isExpired(sub) {
  if (!sub || sub.status === 'cancelled') return true;
  const exp = sub.expiraEm?.toDate?.() || new Date(sub.expiraEm);
  return new Date() > exp;
}

function daysRemaining(sub) {
  if (!sub) return 0;
  const exp = sub.expiraEm?.toDate?.() || new Date(sub.expiraEm);
  const diff = exp - new Date();
  return Math.max(0, Math.ceil(diff / 86400000));
}

const AVATAR_COLORS = ['#3B82F6', '#8B5CF6', '#14B8A6', '#F43F5E', '#F97316', '#EC4899'];
function getColor(str) {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function getIniciais(name) {
  return (name || 'U').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

/* ── Tabs ── */
const TABS = [
  { key: 'visao-geral', label: '📊 Visão Geral' },
  { key: 'usuarios', label: '👥 Usuários & Planos' },
  { key: 'blog', label: '📰 Blog' },
  { key: 'sistema', label: '⚙️ Sistema' },
];

/* ── Shared styles ── */
const labelStyle = {
  display: 'block', fontSize: '0.72rem', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.06em',
  color: 'var(--text-muted)', marginBottom: 6,
};
const inputStyle = {
  width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-xs)', fontFamily: 'var(--font)', fontSize: '0.88rem',
  outline: 'none', background: 'var(--white)', color: 'var(--text)',
  boxSizing: 'border-box',
};
const selectStyle = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 8,
  fontFamily: 'var(--font)', fontSize: '0.75rem', background: 'var(--white)',
  cursor: 'pointer', outline: 'none',
};
const badgeStyle = (bg, color) => ({
  fontSize: '0.68rem', fontWeight: 700, padding: '3px 10px', borderRadius: 100,
  background: bg, color, display: 'inline-block', whiteSpace: 'nowrap',
});

/* ══════════════════════════════════════════════════
   ADMIN PAGE — Exclusivo para Super Admin
══════════════════════════════════════════════════ */
export default function AdminPage() {
  const navigate = useNavigate();
  const { isSuperAdmin, profile, loading: profileLoading } = useUserProfile();
  const [tab, setTab] = useState('visao-geral');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroPlano, setFiltroPlano] = useState('todos');
  const [filtroRole, setFiltroRole] = useState('todos');
  const [msg, setMsg] = useState(null);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 4000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, p, c] = await Promise.all([
        getAdminStats(),
        getAllUsersWithSubscription(),
        getAllPosts(),
        getSystemConfig(),
      ]);
      setStats(s);
      setUsers(u);
      setPosts(p);
      setConfig(c);
    } catch (e) {
      console.error('Admin load error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Bloqueio: só super_admin ── */
  if (profileLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>
        Carregando...
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>
        <span style={{ fontSize: '3rem' }}>🛡️</span>
        <p style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: 16 }}>Acesso Restrito</p>
        <p style={{ fontSize: '0.88rem', marginTop: 4 }}>
          Apenas Super Administradores podem acessar o painel de gestão da plataforma.
        </p>
      </div>
    );
  }

  /* ── Filtered users ── */
  const filteredUsers = users.filter(u => {
    const str = `${u.displayName || ''} ${u.email || ''}`.toLowerCase();
    if (!str.includes(busca.toLowerCase())) return false;

    // Filtro de role
    if (filtroRole !== 'todos' && u.role !== filtroRole) return false;

    // Filtro de plano
    if (filtroPlano === 'todos') return true;
    if (filtroPlano === 'sem-plano') return !u.subscription;
    if (filtroPlano === 'expirado') return u.subscription && isExpired(u.subscription);
    return u.subscription?.planoId === filtroPlano;
  });

  /* ── Plan change handler ── */
  const handleChangePlan = async (userId, planoId) => {
    try {
      if (planoId === 'cancelar') {
        await adminCancelUserPlan(userId);
        showMsg('Plano cancelado');
      } else {
        await adminSetUserPlan(userId, planoId);
        showMsg(`Plano alterado para ${PLANOS[planoId]?.nome}`);
      }
      await loadData();
    } catch (e) {
      showMsg(e.message, 'error');
    }
  };

  /* ── Role change handler ── */
  const handleChangeRole = async (userId, newRole) => {
    if (!window.confirm(`Alterar o perfil deste usuário para "${ROLE_LABELS[newRole]}"?`)) return;
    try {
      await adminSetUserRole(userId, newRole);
      showMsg(`Perfil alterado para ${ROLE_LABELS[newRole]}`);
      await loadData();
    } catch (e) {
      showMsg(e.message, 'error');
    }
  };

  /* ── Blog handlers ── */
  const handleTogglePost = async (post) => {
    await togglePublish(post.id, !post.published);
    await loadData();
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Excluir este post permanentemente?')) return;
    await deletePost(postId);
    await loadData();
  };

  /* ── Config handler ── */
  const handleSaveConfig = async () => {
    try {
      await updateSystemConfig(config);
      showMsg('Configurações salvas!');
    } catch (e) {
      showMsg(e.message, 'error');
    }
  };

  return (
    <div>
      {/* Super Admin badge */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 20, flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.4rem' }}>🛡️</span>
          <div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>Painel Super Admin</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              Gestão da plataforma SimulaBank
            </div>
          </div>
        </div>
        <span style={badgeStyle('rgba(239,68,68,0.1)', '#DC2626')}>
          🔑 Super Admin
        </span>
      </div>

      {/* Message */}
      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: 10, marginBottom: 16, fontSize: '0.82rem', fontWeight: 600,
          background: msg.type === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(0,214,143,0.08)',
          color: msg.type === 'error' ? '#DC2626' : '#059669',
          border: `1px solid ${msg.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(0,214,143,0.2)'}`,
        }}>
          {msg.type === 'error' ? '⚠️' : '✅'} {msg.text}
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs" style={{
        display: 'flex', gap: 4, marginBottom: 24, background: 'var(--white)',
        borderRadius: 12, padding: 4, border: '1px solid var(--border)', overflowX: 'auto',
      }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '10px 16px', border: 'none', borderRadius: 10,
              fontFamily: 'var(--font)', fontSize: '0.82rem', fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap',
              background: tab === t.key ? 'var(--accent)' : 'transparent',
              color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)',
              transition: 'all 0.2s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          Carregando dados administrativos...
        </div>
      ) : (
        <>
          {/* ═══════════════════════════════
             VISÃO GERAL
          ═══════════════════════════════ */}
          {tab === 'visao-geral' && stats && (
            <>
              {/* Stats grid */}
              <div className="admin-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
                {[
                  { label: 'Usuários', value: fmt(stats.totalUsuarios), cor: 'var(--accent)', icon: '👥' },
                  { label: 'Clientes', value: fmt(stats.totalClientes), cor: '#3B82F6', icon: '🤝' },
                  { label: 'Simulações', value: fmt(stats.totalSimulacoes), cor: '#8B5CF6', icon: '⚡' },
                  { label: 'PDFs Gerados', value: fmt(stats.totalPdfs), cor: '#F59E0B', icon: '📄' },
                ].map(s => (
                  <Card key={s.label} style={{ padding: '18px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: '1.6rem', fontWeight: 700, color: s.cor }}>{s.value}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
                      </div>
                      <span style={{ fontSize: '1.8rem', opacity: 0.2 }}>{s.icon}</span>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Role breakdown + Revenue */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
                {/* Role breakdown */}
                <Card style={{ padding: 24 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
                    🏷️ Perfis de Usuário
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {Object.entries(ROLE_LABELS).map(([key, label]) => {
                      const count = stats.roleBreakdown?.[key] || 0;
                      const rc = ROLE_COLORS[key] || { bg: '#eee', color: '#666' };
                      return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 10, height: 10, borderRadius: '50%', background: rc.color, flexShrink: 0,
                          }} />
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1 }}>{label}</span>
                          <span style={{
                            fontSize: '0.82rem', fontWeight: 700, color: rc.color,
                            padding: '2px 10px', borderRadius: 100, background: rc.bg,
                          }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {/* Revenue */}
                <Card style={{ padding: 24 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
                    💰 Receita Mensal Estimada
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: 700, color: 'var(--accent)' }}>
                    {fmtMoeda(stats.receitaMensal)}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Baseado em assinaturas ativas
                  </div>
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {Object.entries(PLANOS).map(([key, plano]) => {
                        const count = stats.planosAtivos[key] || 0;
                        const pct = stats.totalUsuarios > 0 ? Math.round((count / stats.totalUsuarios) * 100) : 0;
                        return (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{
                              width: 10, height: 10, borderRadius: '50%', background: plano.cor, flexShrink: 0,
                            }} />
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, flex: 1 }}>{plano.nome}</span>
                            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: plano.cor }}>{count}</span>
                            <div style={{
                              width: 60, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden',
                            }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: plano.cor, borderRadius: 3 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
              </div>

              {/* Blog + Trials */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Card style={{ padding: 24 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
                    📰 Blog
                  </div>
                  <div style={{ display: 'flex', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#8B5CF6' }}>{stats.totalPosts}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Total posts</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--accent)' }}>{stats.postsPublicados}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Publicados</div>
                    </div>
                  </div>
                  <Button variant="ghost" onClick={() => setTab('blog')} style={{ marginTop: 16, fontSize: '0.78rem' }}>
                    Gerenciar posts →
                  </Button>
                </Card>

                <Card style={{ padding: 24 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
                    ⏱️ Trials
                  </div>
                  <div style={{ display: 'flex', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#F59E0B' }}>{stats.planosAtivos.trial}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Ativos</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#EF4444' }}>{stats.trialsExpirados}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Expirados</div>
                    </div>
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* ═══════════════════════════════
             USUÁRIOS & PLANOS
          ═══════════════════════════════ */}
          {tab === 'usuarios' && (
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px', borderBottom: '1px solid var(--border)', gap: 10, flexWrap: 'wrap',
              }}>
                <input
                  type="text" placeholder="Buscar por nome ou email..."
                  value={busca} onChange={e => setBusca(e.target.value)}
                  style={{ ...inputStyle, flex: 1, minWidth: 0, maxWidth: 280 }}
                />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Filtro por role */}
                  <select value={filtroRole} onChange={e => setFiltroRole(e.target.value)} style={selectStyle}>
                    <option value="todos">Todos os perfis</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="master">Master</option>
                    <option value="gestor">Gestor</option>
                    <option value="usuario">Usuário</option>
                  </select>
                  {/* Filtro por plano */}
                  <select value={filtroPlano} onChange={e => setFiltroPlano(e.target.value)} style={selectStyle}>
                    <option value="todos">Todos os planos</option>
                    <option value="sem-plano">Sem plano</option>
                    <option value="trial">Trial</option>
                    <option value="starter">Starter</option>
                    <option value="profissional">Profissional</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="expirado">Expirados</option>
                  </select>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {filteredUsers.length} usuário{filteredUsers.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 960 }}>
                  <thead>
                    <tr>
                      {['Usuário', 'Perfil', 'Plano Atual', 'Status', 'Expira em', 'Dias', 'Alterar Perfil', 'Alterar Plano'].map(h => (
                        <th key={h} style={{
                          textAlign: 'left', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)',
                          textTransform: 'uppercase', letterSpacing: '0.05em', padding: '10px 14px',
                          background: 'var(--surface)', borderBottom: '1px solid var(--border)',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                          Nenhum usuário encontrado.
                        </td>
                      </tr>
                    )}
                    {filteredUsers.map(u => {
                      const sub = u.subscription;
                      const expired = sub ? isExpired(sub) : false;
                      const days = sub ? daysRemaining(sub) : 0;
                      const planoInfo = sub ? PLANOS[sub.planoId] : null;
                      const rc = ROLE_COLORS[u.role] || ROLE_COLORS.usuario;
                      const isSelf = u.id === profile?.uid;

                      return (
                        <tr key={u.id} style={{ borderBottom: '1px solid var(--border)', opacity: isSelf ? 0.6 : 1 }}>
                          {/* Usuário */}
                          <td style={{ padding: '12px 14px', fontSize: '0.82rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{
                                width: 32, height: 32, borderRadius: 8, background: getColor(u.displayName || u.email),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.65rem', fontWeight: 700, color: '#fff', flexShrink: 0,
                              }}>{getIniciais(u.displayName || u.email)}</div>
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {u.displayName || '—'}
                                  {isSelf && <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginLeft: 6 }}>(você)</span>}
                                </div>
                                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{u.email}</div>
                              </div>
                            </div>
                          </td>

                          {/* Perfil (Role) */}
                          <td style={{ padding: '12px 14px' }}>
                            <span style={badgeStyle(rc.bg, rc.color)}>
                              {ROLE_LABELS[u.role] || u.role}
                            </span>
                          </td>

                          {/* Plano */}
                          <td style={{ padding: '12px 14px' }}>
                            {u.role === 'super_admin' ? (
                              <span style={{ fontSize: '0.72rem', color: '#DC2626', fontWeight: 600 }}>N/A</span>
                            ) : planoInfo ? (
                              <span style={badgeStyle(planoInfo.cor + '18', planoInfo.cor)}>
                                {planoInfo.nome}
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Sem plano</span>
                            )}
                          </td>

                          {/* Status */}
                          <td style={{ padding: '12px 14px' }}>
                            {u.role === 'super_admin' ? (
                              <span style={badgeStyle('rgba(239,68,68,0.1)', '#DC2626')}>Admin</span>
                            ) : (
                              <span style={{
                                ...badgeStyle(
                                  !sub ? 'rgba(100,116,139,0.1)' : expired ? 'rgba(239,68,68,0.1)' : sub.status === 'trial' ? 'rgba(245,158,11,0.1)' : 'rgba(0,214,143,0.1)',
                                  !sub ? '#64748B' : expired ? '#EF4444' : sub.status === 'trial' ? '#D97706' : '#059669'
                                ),
                              }}>
                                {!sub ? 'Inativo' : expired ? 'Expirado' : sub.status === 'trial' ? 'Trial' : sub.status === 'cancelled' ? 'Cancelado' : 'Ativo'}
                              </span>
                            )}
                          </td>

                          {/* Expira em */}
                          <td style={{ padding: '12px 14px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {u.role === 'super_admin' ? '—' : sub ? formatDate(sub.expiraEm) : '—'}
                          </td>

                          {/* Dias restantes */}
                          <td style={{ padding: '12px 14px' }}>
                            {u.role === 'super_admin' ? '—' : sub && !expired ? (
                              <span style={{
                                fontSize: '0.82rem', fontWeight: 700,
                                color: days <= 3 ? '#EF4444' : days <= 7 ? '#F59E0B' : 'var(--accent)',
                              }}>{days}d</span>
                            ) : '—'}
                          </td>

                          {/* Alterar perfil */}
                          <td style={{ padding: '12px 14px' }}>
                            <select
                              value={u.role}
                              onChange={e => handleChangeRole(u.id, e.target.value)}
                              disabled={isSelf}
                              style={{ ...selectStyle, opacity: isSelf ? 0.4 : 1 }}
                            >
                              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </select>
                          </td>

                          {/* Alterar plano */}
                          <td style={{ padding: '12px 14px' }}>
                            {u.role === 'super_admin' ? (
                              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>—</span>
                            ) : (
                              <select
                                value={sub?.planoId || ''}
                                onChange={e => handleChangePlan(u.id, e.target.value)}
                                style={selectStyle}
                              >
                                <option value="" disabled>Selecionar...</option>
                                {Object.entries(PLANOS).map(([key, p]) => (
                                  <option key={key} value={key}>{p.nome} — {fmtMoeda(p.preco)}</option>
                                ))}
                                {sub && sub.status !== 'cancelled' && (
                                  <option value="cancelar">❌ Cancelar plano</option>
                                )}
                              </select>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* ═══════════════════════════════
             BLOG
          ═══════════════════════════════ */}
          {tab === 'blog' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', gap: 16, fontSize: '0.82rem' }}>
                  <span><strong style={{ color: '#8B5CF6' }}>{posts.length}</strong> total</span>
                  <span><strong style={{ color: 'var(--accent)' }}>{posts.filter(p => p.published).length}</strong> publicados</span>
                  <span><strong style={{ color: '#F59E0B' }}>{posts.filter(p => !p.published).length}</strong> rascunhos</span>
                </div>
                <Button variant="accent" onClick={() => navigate('/blog-admin')}>
                  ✏️ Ir para Editor
                </Button>
              </div>

              <Card style={{ padding: 0, overflow: 'hidden' }}>
                {posts.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                    Nenhum post criado ainda.
                  </div>
                ) : (
                  posts.map(post => (
                    <div
                      key={post.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 16,
                        padding: '14px 20px', borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <div style={{
                        width: 48, height: 32, borderRadius: 6, background: post.coverGradient,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <span style={{ fontSize: '1rem' }}>{post.coverEmoji}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{post.title}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {post.category} · {post.readTime} · {post.date}
                        </div>
                      </div>
                      <span style={{
                        ...badgeStyle(
                          post.published ? 'rgba(0,214,143,0.1)' : 'rgba(245,158,11,0.1)',
                          post.published ? '#059669' : '#D97706'
                        ),
                        fontSize: '0.62rem', flexShrink: 0,
                      }}>
                        {post.published ? 'Publicado' : 'Rascunho'}
                      </span>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button onClick={() => handleTogglePost(post)} title={post.published ? 'Despublicar' : 'Publicar'}
                          style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {post.published ? '📤' : '📝'}
                        </button>
                        <button onClick={() => window.open(`/blog/${post.slug}`, '_blank')} title="Ver"
                          style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          👁️
                        </button>
                        <button onClick={() => handleDeletePost(post.id)} title="Excluir"
                          style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </Card>
            </>
          )}

          {/* ═══════════════════════════════
             SISTEMA
          ═══════════════════════════════ */}
          {tab === 'sistema' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Trial config */}
              <Card style={{ padding: 24 }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 20 }}>⏱️ Configuração do Trial</div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Duração do teste grátis (dias)</label>
                  <input
                    type="number" min="1" max="90"
                    value={config.trialDias || 7}
                    onChange={e => setConfig(c => ({ ...c, trialDias: parseInt(e.target.value) || 7 }))}
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Limite de simulações no trial</label>
                  <input
                    type="number" min="1"
                    value={config.trialLimiteSimulacoes || 10}
                    onChange={e => setConfig(c => ({ ...c, trialLimiteSimulacoes: parseInt(e.target.value) || 10 }))}
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Limite de clientes no trial</label>
                  <input
                    type="number" min="1"
                    value={config.trialLimiteClientes || 5}
                    onChange={e => setConfig(c => ({ ...c, trialLimiteClientes: parseInt(e.target.value) || 5 }))}
                    style={inputStyle}
                  />
                </div>

                {/* Botão aplicar a trials existentes */}
                <button
                  onClick={async () => {
                    try {
                      await handleSaveConfig();
                      const count = await recalculateActiveTrials();
                      showMsg(`Configurações aplicadas a ${count} trial(s) ativo(s)!`);
                      await loadData();
                    } catch (e) {
                      showMsg(e.message, 'error');
                    }
                  }}
                  style={{
                    width: '100%', padding: '10px 16px', borderRadius: 8,
                    background: 'rgba(245,158,11,0.08)', border: '1.5px solid rgba(245,158,11,0.2)',
                    color: '#D97706', fontFamily: 'var(--font)', fontSize: '0.78rem',
                    fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                    marginTop: 4,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,158,11,0.15)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(245,158,11,0.08)'}
                >
                  🔄 Salvar e aplicar a trials ativos
                </button>
              </Card>

              {/* System config */}
              <Card style={{ padding: 24 }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 20 }}>⚙️ Configurações Gerais</div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.manutencao || false}
                      onChange={e => setConfig(c => ({ ...c, manutencao: e.target.checked }))}
                      style={{ width: 18, height: 18, accentColor: 'var(--accent)' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>Modo manutenção</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        Bloqueia acesso ao sistema (exceto Super Admin)
                      </div>
                    </div>
                  </label>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.cadastroAberto !== false}
                      onChange={e => setConfig(c => ({ ...c, cadastroAberto: e.target.checked }))}
                      style={{ width: 18, height: 18, accentColor: 'var(--accent)' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>Cadastro aberto</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        Permite novos cadastros na plataforma
                      </div>
                    </div>
                  </label>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.trialAutomatico !== false}
                      onChange={e => setConfig(c => ({ ...c, trialAutomatico: e.target.checked }))}
                      style={{ width: 18, height: 18, accentColor: 'var(--accent)' }}
                    />
                    <div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>Trial automático</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        Ativa trial automaticamente no cadastro
                      </div>
                    </div>
                  </label>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Mensagem de manutenção</label>
                  <textarea
                    value={config.manutencaoMsg || ''}
                    onChange={e => setConfig(c => ({ ...c, manutencaoMsg: e.target.value }))}
                    placeholder="Sistema em manutenção. Voltamos em breve!"
                    style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                  />
                </div>
              </Card>

              {/* Plans overview */}
              <Card style={{ padding: 24, gridColumn: '1 / -1' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 20 }}>💳 Planos Disponíveis</div>
                <div className="admin-plans-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                  {Object.entries(PLANOS).map(([key, plano]) => (
                    <div key={key} style={{
                      padding: 20, borderRadius: 'var(--radius)', border: '2px solid',
                      borderColor: plano.cor + '40', background: plano.cor + '08',
                    }}>
                      <div style={{
                        fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.08em', color: plano.cor, marginBottom: 8,
                      }}>{plano.nome}</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: 4 }}>
                        {plano.preco === 0 ? 'Grátis' : fmtMoeda(plano.preco)}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                        {plano.preco === 0 ? `${plano.duracao} dias` : '/mês'}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {plano.features.map(f => (
                          <div key={f} style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                            ✓ {f}
                          </div>
                        ))}
                      </div>
                      <div style={{
                        marginTop: 12, fontSize: '0.82rem', fontWeight: 700, color: plano.cor, textAlign: 'center',
                      }}>
                        {stats?.planosAtivos[key] || 0} ativo{(stats?.planosAtivos[key] || 0) !== 1 ? 's' : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Save */}
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="accent" onClick={handleSaveConfig}>
                  💾 Salvar Configurações
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <style>{`
        @media(max-width:768px){
          .admin-stats{grid-template-columns:repeat(2,1fr)!important}
          .admin-tabs{overflow-x:auto!important;-webkit-overflow-scrolling:touch}
          .admin-tabs button{min-width:120px}
          .admin-plans-grid{grid-template-columns:repeat(2,1fr)!important}
        }
      `}</style>
    </div>
  );
}